// Integration.js
// Purpose: Manages third-party integrations (e.g., Google Search Console, Notion) for the workspace.
// Logic: Uses React hooks and state to handle authentication, display integration status, and manage related modals and forms.

import { useEffect, useState, useRef } from "react";
import { Button, Drawer, Modal } from "flowbite-react";
import { Accordion } from "flowbite-react";
import useCommonApi from "../hooks/common-apis";
import SuccessModal from "../components/SuccessModal";
import FailureModal from "../components/FailureModal";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Tooltip } from "react-tooltip";
import Loader from "../components/Loader";
import {
  clog,
  getPlanDetails,
  hasMenuAccess,
  isUserOwnerOfWorkspace,
} from "../lib/utils";
import UpgradeModal from "../components/UpgradeModal";

// Upgrade badge component for integrations
const UpgradeBadge = ({ onUpgradeClick }) => (
  <div
    onClick={onUpgradeClick}
    // className="absolute cursor-pointer top-2 right-2 z-10 group-hover:block hidden"
  >
    <span className="bg-gradient-to-r from-[#5E78F4] to-[#F14CE2] text-white font-bold px-2 py-2 rounded-full shadow-lg cursor-pointer hover:scale-105 transition-transform text-xs">
      UPGRADE TO CONNECT
    </span>
  </div>
);

function IntegrationSales() {
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenApolloPreferences, setIsOpenApolloPreferences] = useState(false);
  const [isOpenZohoPreferences, setIsOpenZohoPreferences] = useState(false);
  const [isLoadingSlackDissconnect, setisLoadingSlackDissconnect] =
    useState(false);
  const [wordpressLoading, setwordpressLoading] = useState(false);
  const [wordpressLoadingRegenrate, setwordpressLoadingRegenrate] =
    useState(false);
  const [deleteUrl, setdeleteUrl] = useState(false);
  const [slackPreferences, setSlackPreferences] = useState({
    // send_person_level_identity: true,
    // send_company_level_identity: false,
    // send_daily_credit_consumption: true,
  });
  const [apolloPreferences, setApolloPreferences] = useState({});
  const [zohoPreferences, setZohoPreferences] = useState({});

  const navigate = useNavigate();

  const handleClose = () => setIsOpen(false);
  // offcanvas
  const [openModal, setOpenModal] = useState();
  const [slackProfile, setSlackProfile] = useState();
  const [apolloProfile, setApolloProfile] = useState();
  // Apollo disconnect confirmation modal
  const [openApolloConfirmModal, setOpenApolloConfirmModal] = useState(false);
  const handleCloseApolloConfirm = () => setOpenApolloConfirmModal(false);
  const handleShowApolloConfirm = () => setOpenApolloConfirmModal(true);
  const handleConfirmDisconnectApollo = () => {
    setOpenApolloConfirmModal(false);
    dissconnectApollo();
  };
  const [zohoProfile, setZohoProfile] = useState();
  //zoho disconnect confirmation modal
  const [openZohoConfirmModal, setOpenZohoConfirmModal] = useState(false);
  const handleCloseZohoConfirm = () => setOpenZohoConfirmModal(false);
  const handleShowZohoConfirm = () => setOpenZohoConfirmModal(true);
  const handleConfirmDisconnectZoho = () => {
    setOpenZohoConfirmModal(false);
    disconnectZoho();
  };
  const [checking, setChecking] = useState(false);
  const [openSuccessModal, setopenSuccessModal] = useState(undefined);
  const [openFailureModal, setopenFailureModal] = useState(undefined);
  const [successFailureMessage, setsuccessFailureMessage] = useState("");
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  const removeModal = () => {
    setopenSuccessModal(undefined);
    setopenFailureModal(undefined);
  };

  const handleTogglePreference = async (preference) => {
    // setSlackPreferences((prev) => ({
    //   ...prev,
    //   [preference]: !prev[preference],
    // }));
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/slack/settings`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
        data: {
          id: slackProfile?.id,
          settings: {
            ...slackPreferences,
            [preference]: !slackPreferences[preference],
          },
        },
      });
      if (!response.data.error) {
        setSlackPreferences((prev) => ({
          ...prev,
          [preference]: !prev[preference],
        }));
      } else {
        setopenFailureModal("dismissible");
        setsuccessFailureMessage(response.data?.message);
      }
    } catch (error) {
      clog(error);
      if (error.response && error?.response?.status == 401) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      }
      setopenFailureModal("dismissible");
      setsuccessFailureMessage("Failed to update Slack preferences.");
    } finally {
      // setIsOpen(false);
      checkProfile();
    }
  };

  const handleToggleApolloPreference = async (preference) => {
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/slack/settings`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
        data: {
          id: apolloProfile?.id,
          settings: {
            ...apolloPreferences,
            [preference]: !apolloPreferences[preference],
          },
        },
      });
      if (!response.data.error) {
        setApolloPreferences((prev) => ({
          ...prev,
          [preference]: !prev[preference],
        }));
      } else {
        setopenFailureModal("dismissible");
        setsuccessFailureMessage(response.data?.message);
      }
    } catch (error) {
      clog(error);
      if (error.response && error?.response?.status == 401) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      }
      setopenFailureModal("dismissible");
      setsuccessFailureMessage("Failed to update Apollo preferences.");
    } finally {
      checkProfile();
    }
  };

  const handleToggleZohoPreference = async (preference) => {
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/slack/settings`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
        data: {
          id: zohoProfile?.id,
          settings: {
            ...zohoPreferences,
            [preference]: !zohoPreferences[preference],
          },
        },
      });
      if (!response.data.error) {
        setZohoPreferences((prev) => ({
          ...prev,
          [preference]: !prev[preference],
        }));
      } else {
        setopenFailureModal("dismissible");
        setsuccessFailureMessage(response.data?.message);
      }
    } catch (error) {
      clog(error);
      if (error.response && error?.response?.status == 401) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      }
      setopenFailureModal("dismissible");
      setsuccessFailureMessage("Failed to update Zoho preferences.");
    } finally {
      checkProfile();
    }
  };

  const handleDisconnectSlack = () => {
    // localStorage.removeItem("slack_access");
    // localStorage.removeItem("slack_navigation");
    // setIsOpen(false);
    // You can add API call here to disconnect from backend
    setisLoadingSlackDissconnect(true);
    try {
      const response = axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/slack/disconnect`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
      });
      if (!response.data?.error) {
        localStorage.removeItem("slack_access");
        localStorage.removeItem("slack_navigation");
        setSlackProfile(null);
        setopenSuccessModal("dismissible");
        setsuccessFailureMessage("Slack disconnected successfully.");
        setIsOpen(false);
      }
    } catch (error) {
      clog(error);
      if (error.response && error?.response?.status == 401) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      } else {
        setopenFailureModal("dismissible");
        setsuccessFailureMessage(
          "Failed to disconnect Slack. Please try again."
        );
      }
    } finally {
      setTimeout(() => {
        setisLoadingSlackDissconnect(false);
        setIsOpen(false);
        checkProfile();
      }, 2000);
    }
  };
  const integrateSlack = async () => {
    // clog(id);
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/slack/install`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
      });
      if (!response.data.error) {
        // localStorage.setItem("slack_navigation", "/integration-sales");
        window.open(response.data?.data?.authUrl, "_self");
      } else {
        setopenFailureModal("dismissible");
        setsuccessFailureMessage(response.data?.message);
      }
    } catch (error) {
      clog(error);
      if (error.response && error?.response?.status == 401) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      }
    }
  };
  const integrateApollo = async () => {
    // clog(id);
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/apollo/api/apollo/auth`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
      });
      if (!response.data.error) {
        // localStorage.setItem("slack_navigation", "/integration-sales");
        window.open(response.data?.data?.authUrl, "_self");
      } else {
        setopenFailureModal("dismissible");
        setsuccessFailureMessage(response.data?.message);
      }
    } catch (error) {
      clog(error);
      if (error.response && error?.response?.status == 401) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      }
    }
  };

  const dissconnectApollo = async () => {
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/apollo/api/apollo/connection`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
      });
      if (!response.data?.error) {
        localStorage.removeItem("apollo_access");
        setApolloProfile(null);
        setopenSuccessModal("dismissible");
        setsuccessFailureMessage("Apollo disconnected successfully.");
        setIsOpenApolloPreferences(false);
      } else {
        setopenFailureModal("dismissible");
        setsuccessFailureMessage(response.data?.message);
      }
    } catch (error) {
      clog(error);
      if (error.response && error?.response?.status == 401) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      } else {
        setopenFailureModal("dismissible");
        setsuccessFailureMessage(
          "Failed to disconnect Apollo. Please try again."
        );
      }
    } finally {
      // setisLoadingSlackDissconnect(false);
      // setIsOpen(false);
      checkProfile();
    }
  };

  async function integrateZoho() {
    // clog(id);
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/zoho/api/auth/zoho`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
      });
      if (!response.data.error) {
        window.open(response.data?.authUrl, "_self");
      } else {
        setopenFailureModal("dismissible");
        setsuccessFailureMessage(response.data?.message);
      }
    } catch (error) {
      clog(error);
      if (error.response && error?.response?.status == 401) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      }
    }
  }

  async function disconnectZoho() {
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/zoho/api/zoho/disconnect`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
      });
      if (!response.data?.error) {
        localStorage.removeItem("zoho_access");
        setZohoProfile(null);
        setopenSuccessModal("dismissible");
        setsuccessFailureMessage("Zoho disconnected successfully.");
        setIsOpenZohoPreferences(false);
      } else {
        setopenFailureModal("dismissible");
        setsuccessFailureMessage(response.data?.message);
      }
    } catch (error) {
      clog(error);
      if (error.response && error?.response?.status == 401) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      } else {
        setopenFailureModal("dismissible");
        setsuccessFailureMessage(
          "Failed to disconnect Zoho. Please try again."
        );
      }
    } finally {
      checkProfile();
    }
  }

  const checkProfile = async () => {
    setChecking(true);
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/sales/connected/profile`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
      });
      if (!response.data?.error) {
        // setSlackProfile(response.data?.data);
        // localStorage.setItem(
        //   "slack_access",
        //   JSON.stringify(response.data?.data?.slack_access || false)
        // );
        let slackAccessObj = response.data?.data?.find(
          (item) => item?.social_type === "slack"
        );
        if (slackAccessObj && slackAccessObj?.social_type === "slack") {
          setSlackProfile(slackAccessObj || {});
          localStorage.setItem(
            "slack_access",
            JSON.stringify(slackAccessObj || "")
          );
          let preference = {};
          if (slackAccessObj?.settings) {
            preference = slackAccessObj?.settings || {};
          }

          // if (
          //   slackAccessObj?.connection_status === "connected" &&
          //   (slackAccessObj?.settings?.connect_channel_status == undefined ||
          //     slackAccessObj?.settings?.connect_channel_status == null)
          // ) {
          //   preference.connect_channel_status = true;
          // }
          setSlackPreferences(preference);
        }
        let apolloAccessObj = response.data?.data?.find(
          (item) => item?.social_type === "apollo"
        );
        if (apolloAccessObj && apolloAccessObj?.social_type === "apollo") {
          setApolloProfile(apolloAccessObj || {});
          localStorage.setItem(
            "apollo_access",
            JSON.stringify(apolloAccessObj || "")
          );
          setApolloPreferences(apolloAccessObj?.settings || {});
        }
        let zohoAccessObj = response.data?.data?.find(
          (item) => item?.social_type === "zoho"
        );
        if (zohoAccessObj && zohoAccessObj?.social_type === "zoho") {
          setZohoProfile(zohoAccessObj || {});
          localStorage.setItem(
            "zoho_access",
            JSON.stringify(zohoAccessObj || "")
          );
          setZohoPreferences(zohoAccessObj?.settings || {});
        }
      } else {
        setSlackProfile(null);
        localStorage.setItem("slack_access", JSON.stringify(""));
        setApolloProfile(null);
        localStorage.setItem("apollo_access", JSON.stringify(""));
        setZohoProfile(null);
        localStorage.setItem("zoho_access", JSON.stringify(""));
      }
    } catch (error) {
      clog(error);
      if (error.response && error?.response?.status == 401) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      }
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    // Check if user is workspace owner
    if (!isUserOwnerOfWorkspace()) {
      navigate("/unauthorized");
      return;
    }

    // if (!slackProfile || !apolloProfile) {
    checkProfile();
    // }
    // const searchParams = new URLSearchParams(window.location.search);
    // let success = searchParams.get("success");
    // //parse success to bool
    // success = success === "true" ? true : false;
    // const error = searchParams.get("error");
    // //if searchparams are present then update the twitter access token
    // if (searchParams.has("success")) {
    //   if (!success) {
    //     clog("error from query", error);
    //     setopenFailureModal("dismissible");
    //     setsuccessFailureMessage(error);
    //   }
    // }
  }, []);
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    let error = searchParams.get("error");
    if (error) {
      error = error?.replaceAll("_", " ")?.toUpperCase();
      setopenFailureModal("dismissible");
      setsuccessFailureMessage(error);
      // remove error from search params after showing error using react router
      setTimeout(() => {
        searchParams.delete("error");
        window.history.replaceState({}, "", `${window.location.pathname}`);
      }, 500);
    }
  }, []);

  return (
    <>
      <h6 className="font-[600] text-[24px]">Sales</h6>
      {/* <p className="font-[500] text-[16px] pt-3">
        Lorem ipsum dolor sit amet, consectetur adipiscing elit.
      </p> */}

      {checking ? (
        // Skeleton layout while integrations are being checked
        <div className="my-[36px] integrationAccordion">
          <div className="mb-6">
            <div className="h-6 w-32 bg-gray-300 rounded skeleton-wave mb-2"></div>
            <div className="h-4 w-64 bg-gray-300 rounded skeleton-wave"></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[...Array(5)].map((_, index) => (
              <div
                key={index}
                className="btnBox p-4 rounded-[20px] border border-[#E9EBF0] bg-[#FAFBFC]"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-gray-300 skeleton-wave"></div>
                  <div className="h-4 w-24 bg-gray-300 rounded skeleton-wave"></div>
                </div>
                <div className="h-4 w-32 bg-gray-300 rounded skeleton-wave mb-2"></div>
                <div className="h-8 w-20 bg-gray-300 rounded-full skeleton-wave mt-2 ml-auto"></div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="my-[36px] integrationAccordion">
          <Accordion className="w-full mb-4">
            <Accordion.Panel>
              <Accordion.Title className="dark:text-[#000]">
                <div className="flex items-center">
                  <svg className="icon me-2 text-[24px]">
                    <use href="#icon_sales_tool"></use>
                  </svg>
                  Tools (Send Website Visitors Identity Directly To The Apps)
                </div>
              </Accordion.Title>
              <Accordion.Content className="contentAcco pt-0">
                <hr />
                <div className="pt-3">
                  <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 btnWrapper">
                    <div
                      onClick={() => {
                        if (slackProfile?.connection_status === "connected") {
                          setIsOpen(true);
                        } else {
                          integrateSlack();
                        }
                      }}
                      className="btnBox cursor-pointer hover:shadow-lg transition-shadow duration-200"
                    >
                      <div className="leftBox">
                        <div className="imgBox">
                          <img
                            src="images/Slack.svg"
                            className="wh-40px"
                            alt="..."
                          />
                        </div>
                      </div>
                      <div className="content">
                        <h6>Slack</h6>
                        {/* <p>Lorem ipsum dolor sit amet</p> */}
                      </div>
                      {(!slackProfile ||
                        slackProfile?.connection_status == "disconnected") && (
                        <div
                          className="connectBox cursor-pointer hover:bg-opacity-90 transition-all duration-200"
                          onClick={(e) => {
                            e.stopPropagation();
                            integrateSlack();
                          }}
                        >
                          <div className="conConnect">Connect</div>
                        </div>
                      )}
                      {slackProfile?.connection_status === "connected" && (
                        <span className="checkTick">
                          <svg className="icon">
                            <use href="#icon_green-check"></use>
                          </svg>
                        </span>
                      )}
                    </div>
                    <div
                      onClick={
                        getPlanDetails() == 6
                          ? () => setIsUpgradeModalOpen(true)
                          : () => {
                              if (
                                apolloProfile?.connection_status === "connected"
                              ) {
                                // handleShowApolloConfirm();
                                setIsOpenApolloPreferences(true);
                              } else {
                                integrateApollo();
                              }
                            }
                      }
                      className="btnBox cursor-pointer hover:shadow-lg transition-shadow duration-200"
                    >
                      <div className="leftBox">
                        <div className="imgBox">
                          <img
                            src="images/appolo.png"
                            className="wh-40px"
                            alt="..."
                          />
                        </div>
                      </div>
                      <div className="content">
                        <h6>Apollo.io</h6>
                        {/* <p>Lorem ipsum dolor sit amet</p> */}
                      </div>
                      {(!apolloProfile ||
                        apolloProfile?.connection_status == "disconnected") && (
                        <div
                          className="connectBox cursor-pointer hover:bg-opacity-90 transition-all duration-200"
                          onClick={(e) => {
                            e.stopPropagation();
                            getPlanDetails() == 6
                              ? setIsUpgradeModalOpen(true)
                              : integrateApollo();
                          }}
                        >
                          {getPlanDetails() == 6 ? (
                            <UpgradeBadge />
                          ) : (
                            <div className="conConnect">Connect</div>
                          )}
                        </div>
                      )}
                      {apolloProfile?.connection_status === "connected" && (
                        <>
                          {/* <div
                            className="connectBox cursor-pointer hover:bg-opacity-90 transition-all duration-200"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShowApolloConfirm();
                            }}
                          >
                            <div className="conConnect">Disconnect</div>
                          </div> */}
                          <span className="checkTick">
                            <svg className="icon">
                              <use href="#icon_green-check"></use>
                            </svg>
                          </span>
                        </>
                      )}
                    </div>
                    {/* <div className="btnBox cursor-pointer hover:shadow-lg transition-shadow duration-200">
                      <div className="leftBox">
                        <div className="imgBox">
                          <img
                            src="images/appolo.png"
                            className="wh-40px"
                            alt="..."
                          />
                        </div>
                      </div>
                      <div className="content">
                        <h6>Apollo.io</h6>
                      </div>
                      <div className="comn-soon">Coming Soon</div>
                    </div> */}

                    <div
                      onClick={
                        getPlanDetails() == 6
                          ? () => setIsUpgradeModalOpen(true)
                          : () => {
                              if (
                                zohoProfile?.connection_status === "connected"
                              ) {
                                // handleShowZohoConfirm();
                                setIsOpenZohoPreferences(true);
                              } else {
                                integrateZoho();
                              }
                            }
                      }
                      className="btnBox cursor-pointer hover:shadow-lg transition-shadow duration-200"
                    >
                      <div className="leftBox">
                        <div className="imgBox">
                          <img
                            src="images/zoho.svg"
                            className="wh-40px"
                            alt="..."
                          />
                        </div>
                      </div>
                      <div className="content">
                        <h6>Zoho CRM</h6>
                        {/* <p>Lorem ipsum dolor sit amet</p> */}
                      </div>
                      {(!zohoProfile ||
                        zohoProfile?.connection_status == "disconnected") && (
                        <div
                          className="connectBox cursor-pointer hover:bg-opacity-90 transition-all duration-200"
                          onClick={(e) => {
                            e.stopPropagation();
                            getPlanDetails() == 6
                              ? setIsUpgradeModalOpen(true)
                              : integrateZoho();
                          }}
                        >
                          {getPlanDetails() == 6 ? (
                            <UpgradeBadge />
                          ) : (
                            <div className="conConnect">Connect</div>
                          )}
                        </div>
                      )}
                      {zohoProfile?.connection_status === "connected" && (
                        <>
                          {/* <div
                            className="connectBox cursor-pointer hover:bg-opacity-90 transition-all duration-200"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShowZohoConfirm();
                            }}
                          >
                            <div className="conConnect">Disconnect</div>
                          </div> */}
                          <span className="checkTick">
                            <svg className="icon">
                              <use href="#icon_green-check"></use>
                            </svg>
                          </span>
                        </>
                      )}
                    </div>

                    <div className="btnBox comText">
                      <div className="leftBox">
                        <div className="imgBox">
                          <img
                            src="images/hubspot.svg"
                            className="wh-40px"
                            alt="..."
                          />
                        </div>
                      </div>
                      <div className="content">
                        <h6>Hubspot</h6>
                        {/* <p>Lorem ipsum dolor sit amet</p> */}
                      </div>
                      <div className="comn-soon">Coming Soon</div>
                    </div>
                    <div className="btnBox comText">
                      <div className="leftBox">
                        <div className="imgBox">
                          <img
                            src="images/salesforge.svg"
                            className="wh-40px"
                            alt="..."
                          />
                        </div>
                      </div>
                      <div className="content">
                        <h6>Salesforce</h6>
                        {/* <p>Lorem ipsum dolor sit amet</p> */}
                      </div>
                      <div className="comn-soon">Coming Soon</div>
                    </div>
                    <div className="btnBox comText">
                      <div className="leftBox">
                        <div className="imgBox">
                          <img
                            src="images/Clay.svg"
                            className="wh-40px"
                            alt="..."
                          />
                        </div>
                      </div>
                      <div className="content">
                        <h6>Clay</h6>
                        {/* <p>Lorem ipsum dolor sit amet</p> */}
                      </div>
                      <div className="comn-soon">Coming Soon</div>
                    </div>
                    <div className="btnBox comText">
                      <div className="leftBox">
                        <div className="imgBox">
                          <img
                            src="images/zapier.svg"
                            className="wh-40px"
                            alt="..."
                          />
                        </div>
                      </div>
                      <div className="content">
                        <h6>Zapier</h6>
                        {/* <p>Lorem ipsum dolor sit amet</p> */}
                      </div>
                      <div className="comn-soon">Coming Soon</div>
                    </div>
                    {/* <div className="btnBox comText">
                      <div className="leftBox">
                        <div className="imgBox">
                          <img
                            src="images/zoho.svg"
                            className="wh-40px"
                            alt="..."
                          />
                        </div>
                      </div>
                      <div className="content">
                        <h6>Zoho</h6>

                      </div>
                      <div className="comn-soon">Coming Soon</div>
                    </div> */}
                  </div>
                </div>
              </Accordion.Content>
            </Accordion.Panel>
          </Accordion>
        </div>
      )}

      <Drawer
        open={isOpen}
        onClose={handleClose}
        className=" w-[40vw] !max-w-[90vw] p-0"
        position="right"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between  bg-[#D9F4FF] p-4">
            <div className="flex items-center gap-5">
              {/* slack svg */}
              <div className="imgBox">
                <img src="images/Slack.svg" className="wh-40px" alt="..." />
              </div>
              <div>
                <h2 className="text-[24px] font-[600] text-[#2a2a2a] mb-1">
                  Slack
                </h2>
                <p className="text-[16px] font-[500] text-[#2a2a2a]">
                  Send Website Visitors Identity Directly To Slack
                </p>
              </div>
            </div>
            <Button
              className="flex items-center !p-2 !w-[30px] !h-[30px] !min-w-0 !bg-transparent hover:!bg-gray-100 !text-gray-500"
              onClick={handleClose}
            >
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </Button>
          </div>

          {/* Content */}
          <div className="flex-1 space-y-8 p-4">
            {/* Connected Channel Section */}
            <div className="space-y-6">
              <h3 className="text-[20px] font-[700] text-[#2a2a2a]">
                {/* Connected Channel "{slackProfile?.social_name || ""}" */}
                Connect channel "{slackProfile?.channel_name || ""}" in
                organization "{slackProfile?.social_name || ""}"
              </h3>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {/* Slack Icon */}
                  <svg className="icon text-[24px]">
                    <use href="#icon_active_domain"></use>
                  </svg>
                  <span className="text-[20px] font-[500] text-[#2a2a2a]">
                    Slack Channel Connection Status
                  </span>
                </div>
                <div className="flex items-center  gap-6">
                  <div className="relative items-center flex">
                    <input
                      type="checkbox"
                      className="sr-only"
                      // checked={localStorage.getItem("slack_access")}
                      checked={slackPreferences?.connect_channel_status}
                      // readOnly
                      onClick={() =>
                        handleTogglePreference("connect_channel_status")
                      }
                    />
                    <div
                      onClick={() =>
                        handleTogglePreference("connect_channel_status")
                      }
                      className={`w-[50px] h-[26px] rounded-full transition-colors duration-200 cursor-pointer ${
                        // localStorage.getItem("slack_access")
                        slackPreferences?.connect_channel_status
                          ? "bg-green-500"
                          : "bg-gray-300"
                      }`}
                    >
                      <div
                        className={`w-[22px] h-[22px] bg-white rounded-full shadow-sm transition-transform duration-200 transform ${
                          // localStorage.getItem("slack_access")
                          slackPreferences?.connect_channel_status
                            ? "translate-x-6"
                            : "translate-x-1"
                        } mt-0.5`}
                      ></div>
                    </div>
                  </div>
                  <span
                    className={`px-3 py-1 rounded text-[8px] font-bold tracking-wide ${
                      // localStorage.getItem("slack_access")
                      slackPreferences?.connect_channel_status
                        ? "bg-[#097ba9] text-white"
                        : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    {/* {localStorage.getItem("slack_access") */}
                    {slackPreferences?.connect_channel_status
                      ? "Enabled"
                      : "Disabled"}
                  </span>
                </div>
              </div>
            </div>

            {/* Data Preference Section */}
            <div className="space-y-6">
              <h3 className="text-[20px] font-[700] text-[#2a2a2a]">
                Data Preference
              </h3>

              {/* Send Person Level Identity */}
              <div className="flex items-center justify-between">
                <div className="flex items-start gap-3">
                  {/* <svg
                    className="w-5 h-5 text-gray-600 mt-1"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                      clipRule="evenodd"
                    />
                  </svg> */}
                  {/* icon_send_person_level */}
                  <svg className="w-5 h-5  mt-1">
                    <use href="#icon_send_person_level"></use>
                  </svg>
                  <div>
                    <h4 className="text-[20px] font-[500] text-[#2a2a2a] mb-1">
                      Send Person Level Identity
                    </h4>
                    <p className="text-[12px] text-[#2a2a2a]">
                      Send a slack message to {slackProfile?.channel_name || ""}{" "}
                      when a visitor is identified
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="relative items-center flex">
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={slackPreferences.send_person_level_identity}
                      onChange={() =>
                        handleTogglePreference("send_person_level_identity")
                      }
                    />
                    <div
                      className={`w-[50px] h-[26px] rounded-full transition-colors duration-200 cursor-pointer ${
                        slackPreferences.send_person_level_identity
                          ? "bg-green-500"
                          : "bg-gray-300"
                      }`}
                      onClick={() =>
                        handleTogglePreference("send_person_level_identity")
                      }
                    >
                      <div
                        className={`w-[22px] h-[22px] bg-white rounded-full shadow-sm transition-transform duration-200 transform ${
                          slackPreferences.send_person_level_identity
                            ? "translate-x-6"
                            : "translate-x-1"
                        } mt-0.5`}
                      ></div>
                    </div>
                  </div>
                  <span
                    className={`px-3 py-1 rounded text-[8px] font-bold tracking-wide ${
                      slackPreferences.send_person_level_identity
                        ? "bg-[#097ba9] text-white"
                        : "bg-[#e62121] text-white"
                    }`}
                  >
                    {slackPreferences.send_person_level_identity
                      ? "Enabled"
                      : "Disabled"}
                  </span>
                </div>
              </div>

              {/* Send Company Level Identity */}
              <div className="flex items-center justify-between">
                <div className="flex items-start gap-3">
                  {/* icon_send_company_level */}
                  <svg className="w-5 h-5 text-gray-600 mt-1">
                    <use href="#icon_send_company_level"></use>
                  </svg>
                  <div>
                    <h4 className="text-[20px] font-[500] text-[#2a2a2a] mb-1">
                      Send Company Level Identity
                    </h4>
                    <p className="text-[12px] text-[#2a2a2a]">
                      Send a slack message to {slackProfile?.channel_name || ""}{" "}
                      when a company is identified
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="relative items-center flex">
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={slackPreferences.send_company_level_identity}
                      onChange={() =>
                        handleTogglePreference("send_company_level_identity")
                      }
                    />
                    <div
                      className={`w-[50px] h-[26px] rounded-full transition-colors duration-200 cursor-pointer ${
                        slackPreferences.send_company_level_identity
                          ? "bg-green-500"
                          : "bg-gray-300"
                      }`}
                      onClick={() =>
                        handleTogglePreference("send_company_level_identity")
                      }
                    >
                      <div
                        className={`w-[22px] h-[22px] bg-white rounded-full shadow-sm transition-transform duration-200 transform ${
                          slackPreferences.send_company_level_identity
                            ? "translate-x-6"
                            : "translate-x-1"
                        } mt-0.5`}
                      ></div>
                    </div>
                  </div>
                  <span
                    className={`px-3 py-1 rounded text-[8px] font-bold tracking-wide ${
                      slackPreferences.send_company_level_identity
                        ? "bg-[#097ba9] text-white"
                        : "bg-[#e62121] text-white"
                    }`}
                  >
                    {slackPreferences.send_company_level_identity
                      ? "Enabled"
                      : "Disabled"}
                  </span>
                </div>
              </div>

              {/* Send Daily Credit Consumption */}
              <div className="flex items-center justify-between">
                <div className="flex items-start gap-3">
                  {/* icon_send_daily */}
                  <svg className="w-5 h-5 text-gray-600 mt-1">
                    <use href="#icon_send_daily"></use>
                  </svg>
                  <div>
                    <h4 className="text-[20px] font-[500] text-[#2a2a2a] mb-1">
                      Send Daily Credit Consumption
                    </h4>
                    <p className="text-[12px] text-[#2a2a2a]">
                      Send a slack message to {slackProfile?.channel_name || ""}{" "}
                      with remaining credit
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="relative items-center flex">
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={slackPreferences.send_daily_credit_consumption}
                      onChange={() =>
                        handleTogglePreference("send_daily_credit_consumption")
                      }
                    />
                    <div
                      className={`w-[50px] h-[26px] rounded-full transition-colors duration-200 cursor-pointer ${
                        slackPreferences.send_daily_credit_consumption
                          ? "bg-green-500"
                          : "bg-gray-300"
                      }`}
                      onClick={() =>
                        handleTogglePreference("send_daily_credit_consumption")
                      }
                    >
                      <div
                        className={`w-[22px] h-[22px] bg-white rounded-full shadow-sm transition-transform duration-200 transform ${
                          slackPreferences.send_daily_credit_consumption
                            ? "translate-x-6"
                            : "translate-x-1"
                        } mt-0.5`}
                      ></div>
                    </div>
                  </div>
                  <span
                    className={`px-3 py-1 rounded text-[8px] font-bold tracking-wide ${
                      slackPreferences.send_daily_credit_consumption
                        ? "bg-[#097ba9] text-white"
                        : "bg-[#e62121] text-white"
                    }`}
                  >
                    {slackPreferences.send_daily_credit_consumption
                      ? "Enabled"
                      : "Disabled"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Disconnect Button */}
          <div className="mt-8 pt-6 border-t border-gray-100 p-4 ">
            <Button
              className="flex items-center justify-center !w-full !h-[50px] !bg-gradient-to-r !from-[#ff1111] !to-[#960000] !text-white !font-bold  !rounded-[73px] hover:!from-[#e60000] hover:!to-[#850000] !transition-all !duration-200"
              onClick={handleDisconnectSlack}
            >
              <svg className="icon me-2 text-[20px]">
                <use href="#icon_disconnet"></use>
              </svg>
              <span className="text-[16px]  text-white font-[500]">
                Disconnect From Slack
              </span>
            </Button>
          </div>
        </div>
      </Drawer>
      {/* apollo preferences modal */}
      <Drawer
        open={isOpenApolloPreferences}
        onClose={() => setIsOpenApolloPreferences(false)}
        className=" w-[40vw] !max-w-[90vw] p-0"
        position="right"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between  bg-[#D9F4FF] p-4">
            <div className="flex items-center gap-5">
              {/* apollo svg */}
              <div className="imgBox">
                <img src="images/appolo.png" className="wh-40px" alt="..." />
              </div>
              <div>
                <h2 className="text-[24px] font-[600] text-[#2a2a2a] mb-1">
                  Apollo.io
                </h2>
                <p className="text-[16px] font-[500] text-[#2a2a2a]">
                  Apollo.io Integration Preferences
                </p>
              </div>
            </div>
            <Button
              className="flex items-center !p-2 !w-[30px] !h-[30px] !min-w-0 !bg-transparent hover:!bg-gray-100 !text-gray-500"
              onClick={() => setIsOpenApolloPreferences(false)}
            >
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </Button>
          </div>

          {/* Content */}
          <div className="flex-1 space-y-8 p-4">
            {/* Connected Channel Section */}
            <div className="space-y-6">
              <h3 className="text-[20px] font-[700] text-[#2a2a2a]">
                Connected to organization "{apolloProfile?.social_type || ""}"
              </h3>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {/* Apollo Icon */}
                  <svg className="icon text-[24px]">
                    <use href="#icon_active_domain"></use>
                  </svg>
                  <span className="text-[20px] font-[500] text-[#2a2a2a]">
                    Apollo Connection Status
                  </span>
                </div>
                <div className="flex items-center  gap-6">
                  <div className="relative items-center flex">
                    <input
                      type="checkbox"
                      className="sr-only"
                      // checked={localStorage.getItem("slack_access")}
                      checked={apolloPreferences?.connect_channel_status}
                      // readOnly
                      onClick={() =>
                        handleToggleApolloPreference("connect_channel_status")
                      }
                    />
                    <div
                      onClick={() =>
                        handleToggleApolloPreference("connect_channel_status")
                      }
                      className={`w-[50px] h-[26px] rounded-full transition-colors duration-200 cursor-pointer ${
                        // localStorage.getItem("slack_access")
                        apolloPreferences?.connect_channel_status
                          ? "bg-green-500"
                          : "bg-gray-300"
                      }`}
                    >
                      <div
                        className={`w-[22px] h-[22px] bg-white rounded-full shadow-sm transition-transform duration-200 transform ${
                          // localStorage.getItem("slack_access")
                          apolloPreferences?.connect_channel_status
                            ? "translate-x-6"
                            : "translate-x-1"
                        } mt-0.5`}
                      ></div>
                    </div>
                  </div>
                  <span
                    className={`px-3 py-1 rounded text-[8px] font-bold tracking-wide ${
                      // localStorage.getItem("slack_access")
                      apolloPreferences?.connect_channel_status
                        ? "bg-[#097ba9] text-white"
                        : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    {/* {localStorage.getItem("slack_access") */}
                    {apolloPreferences?.connect_channel_status
                      ? "Enabled"
                      : "Disabled"}
                  </span>
                </div>
              </div>
            </div>

            {/* Data Preference Section */}
            <div className="space-y-6">
              <h3 className="text-[20px] font-[700] text-[#2a2a2a]">
                Data Preference
              </h3>

              {/* Apollo preferences toggles can be added here similar to Slack */}
              <div className="flex items-center justify-between">
                <div className="flex items-start gap-3">
                  {/* icon_send_person_level */}
                  <svg className="w-5 h-5  mt-1">
                    <use href="#icon_send_person_level"></use>
                  </svg>
                  <div>
                    <h4 className="text-[20px] font-[500] text-[#2a2a2a] mb-1">
                      Send Person Level Identity
                    </h4>
                    <p className="text-[12px] text-[#2a2a2a]">
                      Send a person level identity to Apollo.io when a visitor
                      is identified
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="relative items-center flex">
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={apolloPreferences.send_person_level_identity}
                      onChange={() =>
                        handleToggleApolloPreference(
                          "send_person_level_identity"
                        )
                      }
                    />
                    <div
                      className={`w-[50px] h-[26px] rounded-full transition-colors duration-200 cursor-pointer ${
                        apolloPreferences.send_person_level_identity
                          ? "bg-green-500"
                          : "bg-gray-300"
                      }`}
                      onClick={() =>
                        handleToggleApolloPreference(
                          "send_person_level_identity"
                        )
                      }
                    >
                      <div
                        className={`w-[22px] h-[22px] bg-white rounded-full shadow-sm transition-transform duration-200 transform ${
                          apolloPreferences.send_person_level_identity
                            ? "translate-x-6"
                            : "translate-x-1"
                        } mt-0.5`}
                      ></div>
                    </div>
                  </div>
                  <span
                    className={`px-3 py-1 rounded text-[8px] font-bold tracking-wide ${
                      apolloPreferences.send_person_level_identity
                        ? "bg-[#097ba9] text-white"
                        : "bg-[#e62121] text-white"
                    }`}
                  >
                    {apolloPreferences.send_person_level_identity
                      ? "Enabled"
                      : "Disabled"}
                  </span>
                </div>
              </div>
              {/* Additional Apollo preferences can be added here */}
              <div className="flex items-center justify-between">
                <div className="flex items-start gap-3">
                  {/* icon_send_company_level */}
                  <svg className="w-5 h-5 text-gray-600 mt-1">
                    <use href="#icon_send_company_level"></use>
                  </svg>
                  <div>
                    <h4 className="text-[20px] font-[500] text-[#2a2a2a] mb-1">
                      Send Company Level Identity
                    </h4>
                    <p className="text-[12px] text-[#2a2a2a]">
                      Send a company level identity to Apollo.io when a company
                      is identified
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="relative items-center flex">
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={apolloPreferences.send_company_level_identity}
                      onChange={() =>
                        handleToggleApolloPreference(
                          "send_company_level_identity"
                        )
                      }
                    />
                    <div
                      className={`w-[50px] h-[26px] rounded-full transition-colors duration-200 cursor-pointer ${
                        apolloPreferences.send_company_level_identity
                          ? "bg-green-500"
                          : "bg-gray-300"
                      }`}
                      onClick={() =>
                        handleToggleApolloPreference(
                          "send_company_level_identity"
                        )
                      }
                    >
                      <div
                        className={`w-[22px] h-[22px] bg-white rounded-full shadow-sm transition-transform duration-200 transform ${
                          apolloPreferences.send_company_level_identity
                            ? "translate-x-6"
                            : "translate-x-1"
                        } mt-0.5`}
                      ></div>
                    </div>
                  </div>
                  <span
                    className={`px-3 py-1 rounded text-[8px] font-bold tracking-wide ${
                      apolloPreferences.send_company_level_identity
                        ? "bg-[#097ba9] text-white"
                        : "bg-[#e62121] text-white"
                    }`}
                  >
                    {apolloPreferences.send_company_level_identity
                      ? "Enabled"
                      : "Disabled"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Disconnect Button */}
          <div className="mt-8 pt-6 border-t border-gray-100 p-4 ">
            <Button
              className="flex items-center justify-center !w-full !h-[50px] !bg-gradient-to-r !from-[#ff1111] !to-[#960000] !text-white !font-bold  !rounded-[73px] hover:!from-[#e60000] hover:!to-[#850000] !transition-all !duration-200"
              onClick={handleShowApolloConfirm}
            >
              <svg className="icon me-2 text-[20px]">
                <use href="#icon_disconnet"></use>
              </svg>
              <span className="text-[16px]  text-white font-[500]">
                Disconnect From Apollo.io
              </span>
            </Button>
          </div>
        </div>
      </Drawer>

      {/* zoho preferences modal  */}

      <Drawer
        open={isOpenZohoPreferences}
        onClose={() => setIsOpenZohoPreferences(false)}
        className=" w-[40vw] !max-w-[90vw] p-0"
        position="right"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between  bg-[#D9F4FF] p-4">
            <div className="flex items-center gap-5">
              {/* zoho svg */}
              <div className="imgBox">
                <img src="images/zoho.svg" className="wh-40px" alt="..." />
              </div>
              <div>
                <h2 className="text-[24px] font-[600] text-[#2a2a2a] mb-1">
                  Zoho CRM
                </h2>
                <p className="text-[16px] font-[500] text-[#2a2a2a]">
                  Zoho CRM Integration Preferences
                </p>
              </div>
            </div>
            <Button
              className="flex items-center !p-2 !w-[30px] !h-[30px] !min-w-0 !bg-transparent hover:!bg-gray-100 !text-gray-500"
              onClick={() => setIsOpenZohoPreferences(false)}
            >
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </Button>
          </div>

          {/* Content */}
          <div className="flex-1 space-y-8 p-4">
            {/* Connected Channel Section */}
            <div className="space-y-6">
              <h3 className="text-[20px] font-[700] text-[#2a2a2a]">
                Connected to organization "{zohoProfile?.social_type || ""}"
              </h3>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {/* Zoho Icon */}
                  <svg className="icon text-[24px]">
                    <use href="#icon_active_domain"></use>
                  </svg>
                  <span className="text-[20px] font-[500] text-[#2a2a2a]">
                    Zoho CRM Connection Status
                  </span>
                </div>
                <div className="flex items-center  gap-6">
                  <div className="relative items-center flex">
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={zohoPreferences?.connect_channel_status}
                      // readOnly
                      onClick={() =>
                        handleToggleZohoPreference("connect_channel_status")
                      }
                    />
                    <div
                      onClick={() =>
                        handleToggleZohoPreference("connect_channel_status")
                      }
                      className={`w-[50px] h-[26px] rounded-full transition-colors duration-200 cursor-pointer ${
                        // localStorage.getItem("slack_access")
                        zohoPreferences?.connect_channel_status
                          ? "bg-green-500"
                          : "bg-gray-300"
                      }`}
                    >
                      <div
                        className={`w-[22px] h-[22px] bg-white rounded-full shadow-sm transition-transform duration-200 transform ${
                          // localStorage.getItem("slack_access")
                          zohoPreferences?.connect_channel_status
                            ? "translate-x-6"
                            : "translate-x-1"
                        } mt-0.5`}
                      ></div>
                    </div>
                  </div>
                  <span
                    className={`px-3 py-1 rounded text-[8px] font-bold tracking-wide ${
                      zohoPreferences?.connect_channel_status
                        ? "bg-[#097ba9] text-white"
                        : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    {zohoPreferences?.connect_channel_status
                      ? "Enabled"
                      : "Disabled"}
                  </span>
                </div>
              </div>
            </div>

            {/* Data Preference Section */}
            <div className="space-y-6">
              <h3 className="text-[20px] font-[700] text-[#2a2a2a]">
                Data Preference
              </h3>

              {/* Zoho preferences toggles can be added here similar to Slack */}
              <div className="flex items-center justify-between">
                <div className="flex items-start gap-3">
                  {/* icon_send_person_level */}
                  <svg className="w-5 h-5  mt-1">
                    <use href="#icon_send_person_level"></use>
                  </svg>
                  <div>
                    <h4 className="text-[20px] font-[500] text-[#2a2a2a] mb-1">
                      Send Person Level Identity
                    </h4>
                    <p className="text-[12px] text-[#2a2a2a]">
                      Send a person level identity to Zoho CRM when a visitor is
                      identified
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="relative items-center flex">
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={zohoPreferences.send_person_level_identity}
                      onChange={() =>
                        handleToggleZohoPreference("send_person_level_identity")
                      }
                    />
                    <div
                      className={`w-[50px] h-[26px] rounded-full transition-colors duration-200 cursor-pointer ${
                        zohoPreferences.send_person_level_identity
                          ? "bg-green-500"
                          : "bg-gray-300"
                      }`}
                      onClick={() =>
                        handleToggleZohoPreference("send_person_level_identity")
                      }
                    >
                      <div
                        className={`w-[22px] h-[22px] bg-white rounded-full shadow-sm transition-transform duration-200 transform ${
                          zohoPreferences.send_person_level_identity
                            ? "translate-x-6"
                            : "translate-x-1"
                        } mt-0.5`}
                      ></div>
                    </div>
                  </div>
                  <span
                    className={`px-3 py-1 rounded text-[8px] font-bold tracking-wide ${
                      zohoPreferences.send_person_level_identity
                        ? "bg-[#097ba9] text-white"
                        : "bg-[#e62121] text-white"
                    }`}
                  >
                    {zohoPreferences.send_person_level_identity
                      ? "Enabled"
                      : "Disabled"}
                  </span>
                </div>
              </div>
              {/* Additional Zoho preferences can be added here */}
              <div className="flex items-center justify-between">
                <div className="flex items-start gap-3">
                  {/* icon_send_company_level */}
                  <svg className="w-5 h-5 text-gray-600 mt-1">
                    <use href="#icon_send_company_level"></use>
                  </svg>
                  <div>
                    <h4 className="text-[20px] font-[500] text-[#2a2a2a] mb-1">
                      Send Company Level Identity
                    </h4>
                    <p className="text-[12px] text-[#2a2a2a]">
                      Send a company level identity to Zoho CRM when a company
                      is identified
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="relative items-center flex">
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={zohoPreferences.send_company_level_identity}
                      onChange={() =>
                        handleToggleZohoPreference(
                          "send_company_level_identity"
                        )
                      }
                    />
                    <div
                      className={`w-[50px] h-[26px] rounded-full transition-colors duration-200 cursor-pointer ${
                        zohoPreferences.send_company_level_identity
                          ? "bg-green-500"
                          : "bg-gray-300"
                      }`}
                      onClick={() =>
                        handleToggleZohoPreference(
                          "send_company_level_identity"
                        )
                      }
                    >
                      <div
                        className={`w-[22px] h-[22px] bg-white rounded-full shadow-sm transition-transform duration-200 transform ${
                          zohoPreferences.send_company_level_identity
                            ? "translate-x-6"
                            : "translate-x-1"
                        } mt-0.5`}
                      ></div>
                    </div>
                  </div>
                  <span
                    className={`px-3 py-1 rounded text-[8px] font-bold tracking-wide ${
                      zohoPreferences.send_company_level_identity
                        ? "bg-[#097ba9] text-white"
                        : "bg-[#e62121] text-white"
                    }`}
                  >
                    {zohoPreferences.send_company_level_identity
                      ? "Enabled"
                      : "Disabled"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Disconnect Button */}
          <div className="mt-8 pt-6 border-t border-gray-100 p-4 ">
            <Button
              className="flex items-center justify-center !w-full !h-[50px] !bg-gradient-to-r !from-[#ff1111] !to-[#960000] !text-white !font-bold  !rounded-[73px] hover:!from-[#e60000] hover:!to-[#850000] !transition-all !duration-200"
              onClick={handleShowZohoConfirm}
            >
              <svg className="icon me-2 text-[20px]">
                <use href="#icon_disconnet"></use>
              </svg>
              <span className="text-[16px]  text-white font-[500]">
                Disconnect From Zoho CRM
              </span>
            </Button>
          </div>
        </div>
      </Drawer>

      <FailureModal
        open={openFailureModal}
        removeModal={removeModal}
        successFailureMessage={successFailureMessage}
      />
      <SuccessModal
        open={openSuccessModal}
        removeModal={removeModal}
        successFailureMessage={successFailureMessage}
      />
      {/* Apollo Disconnect Confirmation Modal */}
      <Modal
        dismissible
        show={openApolloConfirmModal}
        size="md"
        onClose={handleCloseApolloConfirm}
      >
        <Modal.Body className="p-0">
          <div className="p-4 pb-8 pt-10">
            <h3 className="text-center font-bold text-[26px]">
              Confirm Disconnect
            </h3>
            <p className="text-center font-bold text-[20px] text-[#7D7D7D]">
              Do you really want to disconnect Apollo.io?
            </p>
            <div className="pt-4 flex justify-between px-4 space-x-2">
              <button
                type="button"
                className="flex w-[180px] items-center justify-center py-2 px-[20px] rounded-[20px] font-semibold text-black border border-[black] hover:bg-gray-200"
                onClick={handleCloseApolloConfirm}
              >
                Cancel
              </button>
              <button
                type="button"
                className="flex w-[180px] items-center justify-center py-2 px-[20px] rounded-[20px] font-semibold text-white bg-red-600 hover:bg-red-700"
                onClick={handleConfirmDisconnectApollo}
              >
                Disconnect
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
      {/* Zoho Disconnect Confirmation Modal */}
      <Modal
        dismissible
        show={openZohoConfirmModal}
        size="md"
        onClose={handleCloseZohoConfirm}
      >
        <Modal.Body className="p-0">
          <div className="p-4 pb-8 pt-10">
            <h3 className="text-center font-bold text-[26px]">
              Confirm Disconnect
            </h3>
            <p className="text-center font-bold text-[20px] text-[#7D7D7D]">
              Do you really want to disconnect Zoho?
            </p>
            <div className="pt-4 flex justify-between px-4 space-x-2">
              <button
                type="button"
                className="flex w-[180px] items-center justify-center py-2 px-[20px] rounded-[20px] font-semibold text-black border border-[black] hover:bg-gray-200"
                onClick={handleCloseZohoConfirm}
              >
                Cancel
              </button>
              <button
                type="button"
                className="flex w-[180px] items-center justify-center py-2 px-[20px] rounded-[20px] font-semibold text-white bg-red-600 hover:bg-red-700"
                onClick={handleConfirmDisconnectZoho}
              >
                Disconnect
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={isUpgradeModalOpen}
        setIsOpen={setIsUpgradeModalOpen}
      />
    </>
  );
}

export default IntegrationSales;
